import { AuthSessionStatus } from "@sellar-io/common";
import { useQueryClient } from "@tanstack/react-query";
import { captureMessage } from "@sentry/browser";
import { fetchAuthSession } from "./api/v2/auth/fetchAuthSession";
import { ServerStateKeys } from "./api/v2/ServerStateKeys";
import { simulateSessionFailure } from "./sessionTestControls";
import type { UserCreateResponseType } from "@sellar-io/api-v2/types";
import { setUserProperties } from "firebase/analytics";
import {
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  createContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import {
  FirebaseSession,
  getSellarSessionState,
  SellarSessionContext,
} from "../contexts/SellarSessionContext";
import { SIGNUP } from "../constants/routes";
import { Mixpanel } from "../utils/mixpanel";
import {
  getBusinessQueryKey,
  useSessionBusiness,
} from "./api/v2/business/useBusiness";
import { useIntercomUpdateCurrentBusiness } from "./api/v2/intercom/useIntercomUpdateCurrentBusiness";
import { useUserCreate } from "./api/v2/user/useUserCreate";
import { useEvents } from "./events/useEvents";
import { auth, ExtendedFirebaseUser } from "./useFirebaseUser.shared";
import { SignupFlowStep, useSignupContext } from "./useSignupContext.shared";
import { analytics } from "./useSellarUser.shared";
import {
  getSessionInitializationFailure,
  reportSessionInitializationFailure,
  type SessionInitializationFailure,
  type SessionInitializationStage,
} from "./sessionInitializationFailure";

// Optional telemetry must not turn a valid session into an initialization error.
const runSessionTelemetry = (action: () => void) => {
  try {
    action();
  } catch {
    captureMessage("Session telemetry failed", { level: "warning" });
  }
};

const FirebaseBootstrapContext = createContext<FirebaseSession | undefined>(
  undefined,
);

const FirebaseSessionProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [sessionUser, setSessionUser] = useState<ExtendedFirebaseUser>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<SessionInitializationFailure>();
  const [attempt, setAttempt] = useState(0);
  const authenticationGeneration = useRef(0);
  const retry = useCallback(() => {
    setError(undefined);
    setLoading(true);
    setAttempt((current) => current + 1);
  }, []);
  const updateSessionUser = useCallback(
    (user: Pick<ExtendedFirebaseUser, "hasPasswordAuth">) => {
      setSessionUser((current) =>
        current
          ? { ...current, hasPasswordAuth: user.hasPasswordAuth }
          : current,
      );
    },
    [],
  );

  const navigateToSignup = useEffectEvent(() => navigate(SIGNUP));
  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        const currentGeneration = ++authenticationGeneration.current;
        const isCurrent = () =>
          active &&
          authenticationGeneration.current === currentGeneration &&
          auth.currentUser === firebaseUser;
        setSessionUser(undefined);
        setError(undefined);
        setLoading(true);

        const initialize = async () => {
          if (!firebaseUser) return;
          if (!firebaseUser.emailVerified) {
            navigateToSignup();
            return;
          }
          if (!firebaseUser.email)
            throw new Error("Missing authentication email");

          simulateSessionFailure("authentication");
          const idTokenResult = await firebaseUser.getIdTokenResult();
          if (!isCurrent()) return;
          const signInMethods = await fetchSignInMethodsForEmail(
            auth,
            firebaseUser.email,
          ).catch((): string[] => []);
          if (!isCurrent()) return;
          setSessionUser({
            firebaseUser,
            uid: firebaseUser.uid,
            isAdmin: Boolean(idTokenResult.claims.admin),
            hasPasswordAuth: signInMethods.includes(
              EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD,
            ),
          });
          runSessionTelemetry(() => {
            Mixpanel.track("Account Signed In");
            Mixpanel.identify(firebaseUser.email ?? firebaseUser.uid);
            Mixpanel.people.setOnce({
              $name: firebaseUser.displayName,
              $email: firebaseUser.email,
              $firebaseId: firebaseUser.uid,
              $emailVerified: firebaseUser.emailVerified,
            });
          });
        };

        void initialize()
          .catch((cause: unknown) => {
            if (!isCurrent()) return;
            const failure = getSessionInitializationFailure(
              cause,
              "authentication",
            );
            setSessionUser(undefined);
            setError(failure);
            reportSessionInitializationFailure(failure);
          })
          .finally(() => {
            if (isCurrent()) setLoading(false);
          });
      },
      (cause) => {
        if (!active) return;
        authenticationGeneration.current++;
        const failure = getSessionInitializationFailure(
          cause,
          "authentication",
        );
        setSessionUser(undefined);
        setError(failure);
        setLoading(false);
        reportSessionInitializationFailure(failure);
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [attempt]);

  const value = useMemo(
    () => ({
      user: loading ? undefined : sessionUser,
      setUser: updateSessionUser,
      loading,
      error,
      retry,
      auth,
    }),
    [error, loading, retry, sessionUser, updateSessionUser],
  );

  return (
    <FirebaseBootstrapContext.Provider value={value}>
      {children}
    </FirebaseBootstrapContext.Provider>
  );
};

const AuthenticatedSessionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { setStep } = useSignupContext();
  const { track } = useEvents();
  const queryClient = useQueryClient();
  const firebase = useFirebaseSessionContext();
  const { user: firebaseUser, loading: firebaseLoading } = firebase;
  const navigate = useNavigate();
  const userMutation = useUserCreate(firebaseUser);
  const intercomCurrentBusinessMutation =
    useIntercomUpdateCurrentBusiness(firebaseUser);
  const [loadedSession, setLoadedSession] = useState<{
    identity: ExtendedFirebaseUser["firebaseUser"];
    user: UserCreateResponseType;
    businessId: number | undefined;
  }>();
  const sellarUser =
    loadedSession?.identity === firebaseUser?.firebaseUser
      ? (loadedSession?.user ?? null)
      : null;
  const [initializationError, setInitializationError] = useState<{
    identity: ExtendedFirebaseUser["firebaseUser"];
    failure: SessionInitializationFailure;
  }>();
  const sessionError =
    initializationError?.identity === firebaseUser?.firebaseUser
      ? initializationError?.failure
      : undefined;
  const [retry, setRetry] = useState<{
    failure: SessionInitializationFailure;
    startedAt: number;
  }>();
  const retryInFlight = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const initializationGeneration = useRef(0);
  const pendingBusinessId = useRef<number | undefined>(undefined);
  const [storedBusinessId, setStoredBusinessId] = useLocalStorage<
    number | undefined
  >("businessId", undefined);
  const initializedIdentity = loadedSession?.identity;
  const sessionBusinessId = loadedSession?.businessId;

  const businessQuery = useSessionBusiness(
    firebaseUser,
    initializedIdentity === firebaseUser?.firebaseUser
      ? sessionBusinessId
      : undefined,
  );

  const initializeSession = useCallback(
    async (
      user: ExtendedFirebaseUser,
      requestedBusinessId: number | undefined,
      isCurrent: () => boolean,
    ) => {
      let stage: SessionInitializationStage = "session";
      try {
        const request = {
          user,
          requestedBusinessId: requestedBusinessId ?? null,
          savedBusinessId: storedBusinessId ?? null,
        };
        let session = await fetchAuthSession(request);
        if (!isCurrent()) return;
        if (session.status === AuthSessionStatus.NeedsProfile) {
          // Provision only when the read-only contract reports a missing profile.
          // Returning users no longer write their profile during restoration.
          stage = "user";
          await userMutation.mutateAsync({
            name: user.firebaseUser.displayName,
            SupplierId:
              Number.parseInt(
                localStorage.getItem("lastViewedSupplierId") ?? "",
                10,
              ) || undefined,
          });
          if (!isCurrent()) return;
          stage = "session";
          session = await fetchAuthSession(request);
          if (!isCurrent()) return;
          if (session.status === AuthSessionStatus.NeedsProfile)
            throw new Error("Profile provisioning did not complete");
        }
        if (session.business) {
          const queryKey = getBusinessQueryKey({
            userId: user.uid,
            businessId: session.business.id,
          });
          await queryClient.cancelQueries({ queryKey, exact: true });
          if (!isCurrent()) return;
          queryClient.setQueryData(queryKey, session.business);
        }
        queryClient.setQueryData([ServerStateKeys.SellarUser], session.user);
        setLoadedSession({
          identity: user.firebaseUser,
          user: session.user,
          businessId: session.business?.id,
        });
        setStoredBusinessId(session.business?.id);
        pendingBusinessId.current = undefined;
        runSessionTelemetry(() => {
          if (import.meta.env.VITE_ENV === "production") {
            Mixpanel.people.setOnce({ $type: session.user.type });
            setUserProperties(analytics, { userType: session.user.type });
          }
          if (!session.business) return;
          intercomCurrentBusinessMutation.mutate({
            data: { currentBusinessViewing: session.business.type },
          });
          Mixpanel.groups.setGroup(session.business.id);
          Mixpanel.groups.setOnce(session.business.id, {
            $avatar: session.business.logoUrl,
            $email: session.business.email,
            $name: session.business.name,
            $type: session.business.type,
            $websiteUrl: session.business.websiteUrl,
            $awrsNumber: session.business.awrsNumber,
            $createdAt: session.business.createdAt,
            $city: session.business.InvoiceAddress.city,
            $region: session.business.InvoiceAddress.region,
            $country: session.business.InvoiceAddress.country,
          });
          if (import.meta.env.VITE_ENV === "production")
            setUserProperties(analytics, {
              businessType: session.business.type,
            });
        });
      } catch (cause: unknown) {
        if (!isCurrent()) return;
        const failure = getSessionInitializationFailure(cause, stage);
        setInitializationError({ identity: user.firebaseUser, failure });
        reportSessionInitializationFailure(failure);
      }
    },
    [
      queryClient,
      setStoredBusinessId,
      storedBusinessId,
      userMutation,
      intercomCurrentBusinessMutation,
    ],
  );

  const initializeSessionEvent = useEffectEvent((isActive: () => boolean) => {
    const generation = ++initializationGeneration.current;
    pendingBusinessId.current = undefined;

    if (!firebaseUser) {
      setLoadedSession(undefined);
      setInitializationError(undefined);
      return;
    }
    if (firebaseLoading) return;
    setInitializationError(undefined);
    setLoadedSession(undefined);
    return initializeSession(
      firebaseUser,
      undefined,
      () =>
        isActive() &&
        initializationGeneration.current === generation &&
        auth.currentUser === firebaseUser.firebaseUser,
    );
  });
  const invalidateInitialization = useEffectEvent(() => {
    initializationGeneration.current++;
  });
  const firebaseIdentity = firebaseUser?.firebaseUser;
  useEffect(() => {
    let active = true;
    // Start after the authentication transition commits; obsolete scheduled
    // attempts must not reset the next identity's session.
    queueMicrotask(() => {
      if (active) void initializeSessionEvent(() => active);
    });
    return () => {
      active = false;
      invalidateInitialization();
    };
  }, [firebaseLoading, firebaseIdentity, attempt]);

  const loadingUser =
    firebaseLoading ||
    Boolean(
      firebaseUser &&
      initializedIdentity !== firebaseUser.firebaseUser &&
      !sessionError,
    );
  const currentBusiness =
    firebaseUser && initializedIdentity === firebaseUser.firebaseUser
      ? (businessQuery.data ?? null)
      : null;
  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    if (auth.currentUser) return;
    initializationGeneration.current++;
    retryInFlight.current = false;
    pendingBusinessId.current = undefined;
    setRetry(undefined);
    setLoadedSession(undefined);
    setInitializationError(undefined);
    userMutation.reset();
    await queryClient.cancelQueries();
    if (auth.currentUser) return;
    queryClient.clear();
    runSessionTelemetry(() => {
      if (sellarUser) track("Account Signed Out");
      Mixpanel.reset();
    });
    setStep(SignupFlowStep.EmailStep);
    navigate(SIGNUP);
  }, [navigate, queryClient, sellarUser, setStep, track, userMutation]);

  const changeBusiness = useCallback(
    async (businessId: number) => {
      if (!firebaseUser) return;
      pendingBusinessId.current = businessId;
      const generation = ++initializationGeneration.current;
      setInitializationError(undefined);
      setLoadedSession(undefined);
      await initializeSession(
        firebaseUser,
        businessId,
        () =>
          generation === initializationGeneration.current &&
          auth.currentUser === firebaseUser.firebaseUser,
      );
    },
    [firebaseUser, initializeSession],
  );

  const synchronizeBusinessPreference = useEffectEvent(() => {
    if (
      initializedIdentity === firebaseUser?.firebaseUser &&
      initializedIdentity &&
      storedBusinessId !== undefined &&
      storedBusinessId !== sessionBusinessId
    ) {
      void changeBusiness(storedBusinessId);
    }
  });
  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (active) synchronizeBusinessPreference();
    });
    return () => {
      active = false;
    };
  }, [storedBusinessId]);

  const businessError =
    businessQuery.isError && !businessQuery.isFetching
      ? getSessionInitializationFailure(businessQuery.error, "business")
      : undefined;
  useEffect(() => {
    if (businessQuery.isError && !businessQuery.isFetching) {
      reportSessionInitializationFailure(
        getSessionInitializationFailure(businessQuery.error, "business"),
      );
    }
  }, [businessQuery.error, businessQuery.isError, businessQuery.isFetching]);

  const retryInitialization = useCallback(() => {
    if (retryInFlight.current) return;
    const failure = firebase.error ?? sessionError ?? businessError;
    if (!failure) return;
    retryInFlight.current = true;
    setRetry({ failure, startedAt: Date.now() });
    if (firebase.error) {
      firebase.retry();
    } else if (pendingBusinessId.current !== undefined) {
      void changeBusiness(pendingBusinessId.current);
    } else {
      setInitializationError(undefined);
      setLoadedSession(undefined);
      setAttempt((current) => current + 1);
    }
  }, [businessError, changeBusiness, firebase, sessionError]);

  const sellarContext = useMemo(
    () => ({
      user: firebaseUser ? sellarUser : null,
      changeBusiness,
      signOut,
      loading: loadingUser,
      error: sessionError,
    }),
    [
      changeBusiness,
      firebaseUser,
      loadingUser,
      sellarUser,
      signOut,
      sessionError,
    ],
  );
  const currentBusinessContext = useMemo(
    () => ({
      business: currentBusiness,
      loading:
        loadingUser ||
        businessQuery.isLoading ||
        (businessQuery.isError && businessQuery.isFetching),
      error: businessError,
    }),
    [
      businessQuery.isLoading,
      businessQuery.isError,
      businessQuery.isFetching,
      currentBusiness,
      loadingUser,
      businessError,
    ],
  );
  const initializationState = getSellarSessionState({
    firebase: {
      user: firebase.user,
      loading: firebase.loading,
      error: firebase.error,
    },
    sellar: { user: sellarUser, loading: loadingUser, error: sessionError },
    currentBusiness: currentBusinessContext,
  });
  const initializationPending =
    initializationState.status === "restoring-authentication" ||
    initializationState.status === "loading-user" ||
    initializationState.status === "loading-business";
  useEffect(() => {
    if (!retry || initializationPending) return;
    // Keep even an immediately rejected retry visible, and keep waiting if the
    // real request takes longer. A new loading stage cancels this timer.
    const timeout = window.setTimeout(
      () => {
        retryInFlight.current = false;
        setRetry(undefined);
      },
      Math.max(0, 400 - (Date.now() - retry.startedAt)),
    );
    return () => window.clearTimeout(timeout);
  }, [retry, initializationPending]);

  const sessionContext = useMemo(
    () => ({
      firebase,
      sellar: sellarContext,
      currentBusiness: currentBusinessContext,
      state: retry
        ? { status: "retrying-initialization" as const, failure: retry.failure }
        : initializationState,
      retryInitialization,
    }),
    [
      firebase,
      sellarContext,
      currentBusinessContext,
      retry,
      initializationState,
      retryInitialization,
    ],
  );

  return (
    <SellarSessionContext.Provider value={sessionContext}>
      {children}
    </SellarSessionContext.Provider>
  );
};

const useFirebaseSessionContext = () => {
  const context = useContext(FirebaseBootstrapContext);
  if (!context) throw new Error("Missing Firebase session context");
  return context;
};

export const SellarSessionProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => (
  <FirebaseSessionProvider>
    <AuthenticatedSessionProvider>{children}</AuthenticatedSessionProvider>
  </FirebaseSessionProvider>
);
